import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDebounce } from "use-debounce";
import { Order } from "../components/Table";
import {
  DateFilterValue,
  FilterDefinition,
  TableColumn,
} from "../utils/interfaces";

const DEFAULT_PAGE_SIZE = 25;

// Infer filter value type based on filter definition type
export type FilterValue<T extends FilterDefinition["type"]> = T extends "select"
  ? string[] | string
  : T extends "dateRange"
  ? DateFilterValue
  : T extends "boolean"
  ? boolean
  : never;

// Infer filters object from filterKeyMap
export type FiltersFromMap<M extends Record<string, FilterDefinition>> = {
  [K in keyof M]: FilterValue<M[K]["type"]>;
};

export interface UseTableParamsOptions<
  M extends Record<string, FilterDefinition>,
> {
  filterKeyMap: M;
  defaultPageSize?: number;
  defaultOrdering?: {
    orderBy: string;
    order?: Order;
  };
  debounceDelay?: number;
  storageKey?: string;
}

export const getFilterKeyMap = <T extends string>(
  columns: TableColumn<T>[],
) => {
  return columns.reduce<Record<string, FilterDefinition>>((acc, column) => {
    if (column.filter) {
      acc[column.id] = column.filter;
    }
    return acc;
  }, {});
};

export const useTableParams = <M extends Record<string, FilterDefinition>>({
  filterKeyMap,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  defaultOrdering,
  debounceDelay = 500,
  storageKey = "tableOrdering",
}: UseTableParamsOptions<M>) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const generateEmptyFilters = (): FiltersFromMap<M> => {
    const emptyFilters = {} as FiltersFromMap<M>;
    for (const key in filterKeyMap) {
      const def = filterKeyMap[key];
      switch (def.type) {
        case "select":
          emptyFilters[key] = def.options.multiple ? [] : ("" as any);
          break;
        case "dateRange":
          emptyFilters[key] = {
            range: undefined,
            relativeValue: undefined,
          } as any;
          break;
        case "boolean":
          emptyFilters[key] = false as any;
          break;
      }
    }
    return emptyFilters;
  };

  const decodeFilters = (): FiltersFromMap<M> => {
    const filters: FiltersFromMap<M> = {
      ...generateEmptyFilters(),
    };
    for (const key in filterKeyMap) {
      const def = filterKeyMap[key];
      const raw = searchParams.get(key);
      if (!raw) continue;
      try {
        const value = JSON.parse(raw);
        if (def.type === "select") {
          filters[key] = value;
        } else if (def.type === "dateRange") {
          const val: DateFilterValue = {};
          if (value.relativeValue) val.relativeValue = value.relativeValue;
          if (value.range?.from && value.range?.to) {
            val.range = {
              from: new Date(value.range.from),
              to: new Date(value.range.to),
            };
          }
          filters[key] = val as any;
        } else if (def.type === "boolean") {
          filters[key] = (value === true || value === "true") as FilterValue<
            M[typeof key]["type"]
          >;
        }
      } catch {
        // ignore
      }
    }
    return filters;
  };

  const decodePagination = () => {
    const page = parseInt(searchParams.get("page") || "0", 10);
    const rowsPerPage = parseInt(
      searchParams.get("rowsPerPage") || `${defaultPageSize}`,
      10,
    );
    return { page, rowsPerPage };
  };

  const decodeSearch = () => searchParams.get("search") || "";

  const decodeOrdering = () => {
    const orderBy =
      searchParams.get("orderBy") ||
      localStorage.getItem(`${storageKey}OrderBy`) ||
      defaultOrdering?.orderBy ||
      "";
    const order =
      (searchParams.get("order") as "asc" | "desc") ||
      (localStorage.getItem(`${storageKey}Order`) as "asc" | "desc") ||
      defaultOrdering?.order ||
      "asc";
    return { orderBy, order };
  };

  const [filters, setFilters] = useState<FiltersFromMap<M>>(decodeFilters);
  const [pagination, setPagination] = useState(decodePagination);
  const [search, setSearch] = useState(decodeSearch);
  const [ordering, setOrdering] = useState(decodeOrdering);
  const [debouncedSearch] = useDebounce(search, debounceDelay);

  const isEmptyValue = (val: any): boolean => {
    if (
      val === undefined ||
      val === null ||
      val === "" ||
      val === false ||
      (Array.isArray(val) && val.length === 0)
    ) {
      return true;
    }

    // Special case for dateRange
    if (typeof val === "object" && val !== null && !Array.isArray(val)) {
      const { range, relativeValue } = val;
      const hasValidRange = range?.from && range?.to;
      const hasRelative = !!relativeValue;
      return !hasValidRange && !hasRelative;
    }

    return false;
  };

  const updateSearchParams = (updated: Partial<Record<string, any>>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updated).forEach(([key, val]) => {
      if (isEmptyValue(val)) {
        newParams.delete(key);
      } else {
        newParams.set(key, typeof val === "string" ? val : JSON.stringify(val));
      }
    });
    setSearchParams(newParams, { replace: true });
  };

  // 👇 Sync filters to query params
  useEffect(() => {
    updateSearchParams(filters);
  }, [filters]);

  useEffect(() => {
    updateSearchParams(pagination);
  }, [pagination]);

  useEffect(() => {
    updateSearchParams(ordering);
  }, [ordering]);

  useEffect(() => {
    updateSearchParams({ search: debouncedSearch });
  }, [debouncedSearch]);

  const handleOrdering = (orderBy: string) => {
    const newOrder =
      ordering.orderBy === orderBy && ordering.order === "asc" ? "desc" : "asc";
    const newOrdering = { orderBy, order: newOrder } as const;
    setOrdering(newOrdering);
    updateSearchParams(newOrdering);
    localStorage.setItem(`${storageKey}OrderBy`, orderBy);
    localStorage.setItem(`${storageKey}Order`, newOrder);
  };

  return {
    filters,
    setFilters,
    search,
    setSearch,
    debouncedSearch,
    pagination,
    setPagination,
    ordering,
    setOrdering: handleOrdering,
  };
};
